/**
 * Campos comuns dos formulários de usuário (visual v2): dados pessoais, função,
 * OAB e situação — usados no cadastro e na edição de usuário.
 */
import { useState, type ReactNode } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { FormField } from '@/components/common/FormSection'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { NativeSelect } from '@/components/ui/native-select'
import { isLawyerLike, isOfficeRole, OFFICE_ROLES, ROLE_LABELS, roleLabel } from '@/lib/roles'

export const BRAZILIAN_STATES = [
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' }
] as const

export interface UserFormValues {
  firstName: string
  lastName: string
  email: string
  password?: string
  role: string
  isActive: boolean
  oabNumber?: string
  oabState?: string
}

export type UserFormErrors = Partial<Record<keyof UserFormValues, string>>

/** Validação comum (sem a senha, que difere entre cadastro e edição). */
export function validateUserFields(values: UserFormValues): UserFormErrors {
  const errors: UserFormErrors = {}

  if (!values.firstName.trim()) errors.firstName = 'Nome é obrigatório'
  if (!values.lastName.trim()) errors.lastName = 'Sobrenome é obrigatório'

  if (!values.email.trim()) {
    errors.email = 'E-mail é obrigatório'
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
    errors.email = 'E-mail inválido'
  }

  // OAB é opcional (há advogado sem OAB cadastrada); se informar, número e UF juntos.
  // As OABs completas (várias por pessoa) ficam em Configurações › Equipe.
  if (isLawyerLike(values.role)) {
    if (values.oabNumber?.trim() && !values.oabState?.trim()) {
      errors.oabState = 'Estado da OAB é obrigatório quando o número é informado'
    }
    if (values.oabState?.trim() && !values.oabNumber?.trim()) {
      errors.oabNumber = 'Número da OAB é obrigatório quando o estado é informado'
    }
  }

  return errors
}

interface PasswordFieldProps {
  id: string
  label: string
  value: string
  placeholder: string
  error?: string
  onChange: (value: string) => void
}

/** Campo de senha com botão de mostrar/ocultar. */
export function PasswordField({ id, label, value, placeholder, error, onChange }: PasswordFieldProps) {
  const [visible, setVisible] = useState(false)
  return (
    <FormField label={label} htmlFor={id} error={error}>
      <div className="relative">
        <Input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          aria-invalid={!!error}
          className="pr-9"
        />
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="absolute right-0.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Ocultar senha' : 'Mostrar senha'}
        >
          {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </Button>
      </div>
    </FormField>
  )
}

interface UserFormFieldsProps {
  idPrefix: string
  values: UserFormValues
  errors: UserFormErrors
  onChange: (patch: Partial<UserFormValues>) => void
  /** Campo(s) de senha, que diferem entre cadastro e edição. */
  passwordSlot: ReactNode
}

export function UserFormFields({ idPrefix, values, errors, onChange, passwordSlot }: UserFormFieldsProps) {
  const id = (field: string) => `${idPrefix}-${field}`
  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="Nome *" htmlFor={id('first-name')} error={errors.firstName}>
          <Input
            id={id('first-name')}
            type="text"
            value={values.firstName}
            onChange={(e) => onChange({ firstName: e.target.value })}
            placeholder="Digite o nome"
            aria-invalid={!!errors.firstName}
          />
        </FormField>
        <FormField label="Sobrenome *" htmlFor={id('last-name')} error={errors.lastName}>
          <Input
            id={id('last-name')}
            type="text"
            value={values.lastName}
            onChange={(e) => onChange({ lastName: e.target.value })}
            placeholder="Digite o sobrenome"
            aria-invalid={!!errors.lastName}
          />
        </FormField>
      </div>

      <FormField label="E-mail *" htmlFor={id('email')} error={errors.email}>
        <Input
          id={id('email')}
          type="email"
          value={values.email}
          onChange={(e) => onChange({ email: e.target.value })}
          placeholder="Digite o e-mail"
          aria-invalid={!!errors.email}
        />
      </FormField>

      {passwordSlot}

      <FormField label="Função" htmlFor={id('role')}>
        <NativeSelect id={id('role')} value={values.role} onChange={(e) => onChange({ role: e.target.value })}>
          {!isOfficeRole(values.role) && (
            <option value={values.role}>{roleLabel(values.role)} (papel antigo)</option>
          )}
          {OFFICE_ROLES.map((r) => (
            <option key={r} value={r}>{ROLE_LABELS[r]}</option>
          ))}
        </NativeSelect>
      </FormField>

      {isLawyerLike(values.role) && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Número da OAB" htmlFor={id('oab-number')} error={errors.oabNumber}>
            <Input
              id={id('oab-number')}
              type="text"
              value={values.oabNumber || ''}
              onChange={(e) => onChange({ oabNumber: e.target.value })}
              placeholder="Ex: 123456"
              aria-invalid={!!errors.oabNumber}
            />
          </FormField>
          <FormField label="Estado da OAB" htmlFor={id('oab-state')} error={errors.oabState}>
            <NativeSelect
              id={id('oab-state')}
              value={values.oabState || ''}
              onChange={(e) => onChange({ oabState: e.target.value })}
              aria-invalid={!!errors.oabState}
            >
              <option value="">Selecione o estado</option>
              {BRAZILIAN_STATES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </NativeSelect>
          </FormField>
        </div>
      )}

      <div className="flex items-center gap-2">
        <input
          id={id('is-active')}
          type="checkbox"
          checked={values.isActive}
          onChange={(e) => onChange({ isActive: e.target.checked })}
          className="size-4 rounded-sm border-input accent-primary"
        />
        <Label htmlFor={id('is-active')} className="font-normal">Usuário ativo</Label>
      </div>
    </>
  )
}
